import { CdkDragDrop, DragDropModule, transferArrayItem } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { combineLatest, debounceTime, from, map, Observable, startWith, Subscription, tap } from 'rxjs';
import { Task, TaskRequest, TaskStatus } from '@workspaces/models/task';
import { AppState } from 'src/app/state/app.state';
import { Store } from '@ngrx/store';
import { taskListFeature, TaskListFetch } from '@workspaces/store/task-list.feature';
import { EditComponent } from '@workspaces/utils/edit-component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Params, Router, RouterModule } from '@angular/router';
import { WorkspaceTaskService } from '@workspaces/services/task.service';
import { KanbanIdPipe } from '@workspaces/utils/pipes/kanban-id.pipe';
import { IsDueDateExpiredPipe } from '@workspaces/utils/pipes/is-due-date-expired.pipe';
import { TaskDetailsDialogComponent } from '../task-details/dialog/task-details-dialog.component';
import { ShortIdPipe } from '../../utils/pipes/short-id.pipe';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { userManagementFeature, UserManagementFetch } from '@workspaces/store/user-management.feature';
import { WorkspaceUser } from '@workspaces/models/users';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { CognitoService } from 'src/app/services/cognito.service';
import { WorkspaceAuthService } from '@workspaces/utils/workspace-auth.service';
import { RequiredRoleDirective } from '@workspaces/utils/workspace-guard.directive';
import { workspaceListFeature } from '@workspaces/store/workspace-list.feature';

@Component({
  selector: 'app-task-kanban',
  templateUrl: './task-kanban.component.html',
  styleUrls: ['./task-kanban.component.scss', '../../global-workspace-styles.scss'],
  standalone: true,
  imports: [
    CommonModule,
    DragDropModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    MatMenuModule,
    KanbanIdPipe,
    IsDueDateExpiredPipe,
    RouterModule,
    ShortIdPipe,
    MatTooltipModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    RequiredRoleDirective
  ]
})
export class TaskKanbanComponent extends EditComponent implements OnInit, OnDestroy {
  private readonly sub = new Subscription();
  readonly taskColumns: { status: TaskStatus; tasks: Task[] }[] = [
    { status: 'todo', tasks: [] },
    { status: 'in_progress', tasks: [] },
    { status: 'done', tasks: [] },
    { status: 'rejected', tasks: [] }
  ];

  connectedTo: string[] = [];
  isDragging = false;
  currentTargetList: string | null = null;
  lastGrabbed: string | null = null;

  readonly selectedUsers = new FormControl<string[]>([]);
  readonly searchControl = new FormControl('');

  users$!: Observable<WorkspaceUser[]>;
  currentUserId = '';
  public isDisabled = false;
  constructor(
    protected override readonly route: ActivatedRoute,
    protected readonly workspaceTaskService: WorkspaceTaskService,
    protected override readonly snackbar: MatSnackBar,
    protected override readonly router: Router,
    private readonly store: Store<AppState>,
    private readonly dialog: MatDialog,
    private readonly cdr: ChangeDetectorRef,
    private readonly cognitoService: CognitoService,
    private readonly workspaceAuthService: WorkspaceAuthService
  ) {
    super(route, snackbar, router);
  }

  override ngOnInit() {
    super.ngOnInit();
    this.setupUserObservable();
    this.subscribeToTaskListChanges();
    this.setInitialStateFromUrl();
    this.subscribeToSearchControl();
    this.subscribeToSelectedUsersControl();
    this.subscribeToRouteQueryParams();
    this.dispatchInitialActions();
    this.sub.add(
      this.store
        .select(workspaceListFeature.workspaceById(this.workspaceId))
        .pipe(
          map(workspace => !!workspace?.is_archived || !!workspace?.is_deleted),
          startWith(false)
        )
        .subscribe(disabled => (this.isDisabled = disabled))
    );
  }

  private setupUserObservable() {
    const userIdPromise = this.cognitoService.getUserId();
    this.users$ = combineLatest([
      this.store.select(userManagementFeature.selectData).pipe(startWith([] as WorkspaceUser[])),
      from(userIdPromise).pipe(startWith(''))
    ]).pipe(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      tap(([_users, currentUserId]) => {
        this.currentUserId = currentUserId;
      }),
      map(([users, currentUserId]) => {
        const currentUserIndex = users.findIndex(user => user.id === currentUserId);

        if (currentUserIndex > -1) {
          const reorderedUsers = [...users];
          const currentUser = reorderedUsers.splice(currentUserIndex, 1)[0];
          reorderedUsers.unshift(currentUser);
          return reorderedUsers;
        }

        return users;
      })
    );
  }

  private subscribeToTaskListChanges() {
    this.sub.add(
      combineLatest([
        this.store.select(taskListFeature.selectData),
        this.selectedUsers.valueChanges.pipe(startWith([] as string[])),
        this.searchControl.valueChanges.pipe(startWith(''))
      ]).subscribe(([taskList, users, searchTerm]) => {
        if (this.workspaceId === taskList.workspaceId) {
          const clonedTasksFromStore = structuredClone(taskList.tasks);
          const responsibleTasks = this.filterResponsible(users, clonedTasksFromStore);
          const searchTasks = this.filterSearch(searchTerm ?? '', responsibleTasks);
          this.updateTaskColumns(searchTasks);
          this.connectedTo = this.taskColumns.map(column => `list-${column.status}`);
        }
      })
    );
  }

  private updateTaskColumns(tasks: Task[]) {
    const taskStatusRecord: Partial<Record<TaskStatus, Task[]>> = {};
    tasks.forEach(task => {
      if (taskStatusRecord[task.status]) {
        taskStatusRecord[task.status]?.push(task);
      } else {
        taskStatusRecord[task.status] = [task];
      }
    });

    this.taskColumns.forEach(task => (task.tasks = []));

    Object.entries(taskStatusRecord).forEach(([taskStatus, task]) => {
      const currentColumn = this.taskColumns.find(column => column.status === taskStatus);
      if (currentColumn) {
        currentColumn.tasks = task;
      }
    });
  }

  private subscribeToSearchControl() {
    this.sub.add(
      this.searchControl.valueChanges.pipe(debounceTime(300)).subscribe(searchTerm => {
        this.updateQueryParams({ search: searchTerm || null });
      })
    );
  }

  private subscribeToSelectedUsersControl() {
    this.sub.add(
      this.selectedUsers.valueChanges.subscribe(users => {
        const usersParam = users && users.length > 0 ? users.join(',') : null;
        this.updateQueryParams({ users: usersParam });
      })
    );
  }

  private subscribeToRouteQueryParams() {
    this.sub.add(
      this.route.queryParams.subscribe(queryParams => {
        const { initialSearch, initialUsers } = this.getStateFromUrl(queryParams);
        // Only patch if the control values are different to prevent infinite loop with control.valueChanges
        if (this.searchControl.value !== initialSearch) {
          this.searchControl.patchValue(initialSearch, { emitEvent: false });
        }
        if (this.selectedUsers.value?.join(',') !== initialUsers?.join(',')) {
          this.selectedUsers.patchValue(initialUsers, { emitEvent: false });
        }
      })
    );
  }

  private dispatchInitialActions() {
    this.store.dispatch(TaskListFetch({ workspaceId: this.workspaceId }));
    this.store.dispatch(UserManagementFetch({ workspaceId: this.workspaceId }));
  }

  private setInitialStateFromUrl() {
    const { initialSearch, initialUsers } = this.getStateFromUrl(this.route.snapshot.queryParams);
    this.searchControl.patchValue(initialSearch, { emitEvent: false });
    this.selectedUsers.patchValue(initialUsers, { emitEvent: false });
  }

  private getStateFromUrl(queryParams: Params) {
    const initialSearch = queryParams['search'] || '';
    const initialUsersRaw = queryParams['users'];
    const initialUsers = initialUsersRaw
      ? Array.isArray(initialUsersRaw)
        ? initialUsersRaw
        : initialUsersRaw.split(',')
      : [];
    return { initialSearch, initialUsers };
  }

  private filterSearch(searchTerm: string, tasks: Task[]): Task[] {
    return tasks.filter(task => {
      const lowerSearchTerm = searchTerm.toLowerCase();
      const matchesTitle = task.name.toLowerCase().includes(lowerSearchTerm);
      const matchesDescription = task.description.toLowerCase().includes(lowerSearchTerm);
      const matchesResponsibleName = task.assigned_to?.name.toLowerCase().includes(lowerSearchTerm);

      return matchesTitle || matchesDescription || matchesResponsibleName;
    });
  }

  public drop(event: CdkDragDrop<Task[]>, targetColumnId: TaskStatus): void {
    this.isDragging = false;
    this.currentTargetList = null;
    if (event.previousContainer !== event.container) {
      this.transferToNewColumn(event, targetColumnId);
    }
  }

  public onDragStart(elementId: string) {
    this.lastGrabbed = elementId;
    this.isDragging = true;
  }

  public onEnter(event: { container: { id: string } }): void {
    this.currentTargetList = event.container.id;
  }

  public onExit(event: { container: { id: string } }) {
    if (this.currentTargetList === event.container.id) {
      this.currentTargetList = null;
    }
  }

  public onDragEnd() {
    this.isDragging = false;
    this.currentTargetList = null;
  }

  private filterResponsible(selectedUserIds: (string | undefined)[] | null, tasks: Task[]): Task[] {
    if (!selectedUserIds?.length) {
      return tasks;
    }
    return tasks.filter(task => selectedUserIds.includes(task.assigned_to?.id));
  }

  private transferToNewColumn(event: CdkDragDrop<Task[]>, targetColumnId: TaskStatus) {
    transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
    const movedTask = event.container.data[event.currentIndex];
    const originalStatus = event.previousContainer.id as TaskStatus;

    if (movedTask) {
      movedTask.status = targetColumnId;
      const mappedTask: TaskRequest = {
        assigned_to_id: movedTask.assigned_to?.id ?? null,
        description: movedTask.description ?? null,
        name: movedTask.name,
        due_date: movedTask.due_date ? new Date(movedTask.due_date).toISOString() : new Date().toISOString(),
        priority: movedTask.priority ?? null,
        status: movedTask.status ?? null,
        tags: movedTask.tags ?? null
      };
      this.sub.add(
        this.workspaceTaskService.updateTask(this.workspaceId, movedTask.task_id, mappedTask).subscribe({
          next: () => {
            this.store.dispatch(TaskListFetch({ workspaceId: this.workspaceId }));
          },
          error: () => {
            transferArrayItem(
              event.container.data,
              event.previousContainer.data,
              event.currentIndex,
              event.previousIndex
            );
            movedTask.status = originalStatus;
            this.cdr.detectChanges();
          }
        })
      );
    }
  }

  private updateQueryParams(params: Record<string, string | null>): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: params,
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  public addNewTask(columnId: string): void {
    const dialogRef = this.dialog.open(TaskDetailsDialogComponent, {
      width: TaskDetailsDialogComponent.width,
      data: { columnId: columnId, workspaceId: this.workspaceId }
    });

    this.sub.add(
      dialogRef.afterClosed().subscribe(() => {
        this.store.dispatch(TaskListFetch({ workspaceId: this.workspaceId }));
      })
    );
  }

  selectUser(selectedUserId: string): void {
    let currentSelectedUserIds = this.selectedUsers.value;
    if (currentSelectedUserIds?.includes(selectedUserId)) {
      currentSelectedUserIds = currentSelectedUserIds.filter(userId => userId !== selectedUserId);
    } else {
      currentSelectedUserIds?.push(selectedUserId);
    }
    this.selectedUsers.patchValue(currentSelectedUserIds);
  }

  public ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}
